import type { PageInfo, PageModel, PtRole, RoleListReq, RolePrivilegeInfo, RolePrivilegeInfoReq } from '@/api/types/pt_entities'
import type { UsableStatus } from '@/api/types/pt_enums'
import type { RequestOptions, ResponseModel } from '@/types/pt_types'
import { apiClient } from '@/axios/http_client'

export interface ChangeRoleStatusParam {
  roleId?: number // 角色编号
  status?: UsableStatus // 角色状态
  options?: RequestOptions
}
export interface CreateRoleParam {
  request?: PtRole // 角色信息
  options?: RequestOptions
}
export interface DeleteRoleParam {
  roleId?: number // 角色编号
  options?: RequestOptions
}
export interface DeleteRolesParam {
  request?: RoleListReq // 删除条件
  options?: RequestOptions
}
export interface GetRoleParam {
  roleId?: number // 角色编号
  options?: RequestOptions
}
export interface GetRolePrivilegeIdsParam {
  roleId?: number // 角色编号
  options?: RequestOptions
}
export interface GetRolesParam {
  request?: RoleListReq // 查询条件
  page?: PageInfo // 分页信息
  options?: RequestOptions
}
export interface GetSelfPrivilegesParam {
  type?: string // 上级权限名称
  options?: RequestOptions
}
export interface RefreshRoleParam {
  roleId?: number // 角色编号
  merchantId?: number // 商户编号
  options?: RequestOptions
}
export interface RefreshRolePrivilegeParam {
  roleIds?: string // 角色编号
  options?: RequestOptions
}
export interface SetRolePrivilegesParam {
  request?: RolePrivilegeInfoReq // 查询条件
  options?: RequestOptions
}
export interface UpdateRoleParam {
  roleId?: number // 角色编号
  request?: PtRole // 角色信息
  options?: RequestOptions
}

/**
 * 
 * Created by hongsq.
 */
class RoleApi {

  /**
   * 修改角色状态 - 修改角色状态
   * @param roleId 角色编号
   * @param status 角色状态
   */
  async changeRoleStatus(params: ChangeRoleStatusParam = {}): Promise<void> {
    const {  roleId, status, options  } = params
    const resp = await apiClient.formPut<ResponseModel<void>>({
      data: {},
      checkToken: true,
      url: `/api/role/${roleId}/${status}`,
      apiOptions: options
    })
    return resp.data!
  }

  /**
   * 新增角色 - 新增角色
   * @param request 角色信息
   */
  async createRole(params: CreateRoleParam = {}): Promise<PtRole> {
    const {  request, options  } = params
    const resp = await apiClient.jsonPost<ResponseModel<PtRole>>({
      data: { ...(request || {}) },
      checkToken: true,
      url: '/api/role',
      apiOptions: options
    })
    return resp.data!
  }

  /**
   * 删除角色 - 删除角色
   * @param roleId 角色编号
   */
  async deleteRole(params: DeleteRoleParam = {}): Promise<void> {
    const {  roleId, options  } = params
    const resp = await apiClient.delete<ResponseModel<void>>({
      params: {},
      checkToken: true,
      url: `/api/role/${roleId}`,
      apiOptions: options
    })
    return resp.data!
  }

  /**
   * 删除角色 - 批量删除角色
   * @param request 删除条件
   */
  async deleteRoles(params: DeleteRolesParam = {}): Promise<void> {
    const {  request, options  } = params
    const resp = await apiClient.delete<ResponseModel<void>>({
      params: { ...(request || {}) },
      checkToken: true,
      url: '/api/role',
      apiOptions: options
    })
    return resp.data!
  }

  /**
   * 查询角色 - 查询角色信息
   * @param roleId 角色编号
   */
  async getRole(params: GetRoleParam = {}): Promise<PtRole> {
    const {  roleId, options  } = params
    const resp = await apiClient.get<ResponseModel<PtRole>>({
      params: {},
      checkToken: true,
      url: `/api/role/${roleId}`,
      apiOptions: options
    })
    return resp.data!
  }

  /**
   * 查询角色权限列表 - 查询角色权限列表
   * @param roleId 角色编号
   */
  async getRolePrivilegeIds(params: GetRolePrivilegeIdsParam = {}): Promise<Array<number>> {
    const {  roleId, options  } = params
    const resp = await apiClient.get<ResponseModel<Array<number>>>({
      params: {},
      checkToken: true,
      url: `/api/role/privilege/${roleId}`,
      apiOptions: options
    })
    return resp.data!
  }

  /**
   * 查询角色列表 - 查询一组角色信息
   * @param request 查询条件
   * @param page 分页信息
   */
  async getRoles(params: GetRolesParam = {}): Promise<PageModel<PtRole>> {
    const {  request, page, options  } = params
    const resp = await apiClient.get<ResponseModel<PageModel<PtRole>>>({
      params: { ...(request || {}), ...(page || {}) },
      checkToken: true,
      url: '/api/role/list',
      apiOptions: options
    })
    return resp.data!
  }

  /**
   * 查询自己的权限列表 - 查询自己的权限列表
   * @param type 上级权限名称
   */
  async getSelfPrivileges(params: GetSelfPrivilegesParam = {}): Promise<Array<RolePrivilegeInfo>> {
    const {  type, options  } = params
    const resp = await apiClient.get<ResponseModel<Array<RolePrivilegeInfo>>>({
      params: { type },
      checkToken: 'MAYBE',
      url: '/api/role/self/privilege',
      apiOptions: options
    })
    return resp.data!
  }

  /**
   * 刷新角色 - 刷新角色信息
   * @param roleId 角色编号
   * @param merchantId 商户编号
   */
  async refreshRole(params: RefreshRoleParam = {}): Promise<void> {
    const {  roleId, merchantId, options  } = params
    const resp = await apiClient.formPut<ResponseModel<void>>({
      data: { roleId, merchantId },
      checkToken: true,
      url: '/api/role/refresh',
      apiOptions: options
    })
    return resp.data!
  }

  /**
   * 刷新角色权限 - 刷新角色权限
   * @param roleIds 角色编号
   */
  async refreshRolePrivilege(params: RefreshRolePrivilegeParam = {}): Promise<void> {
    const {  roleIds, options  } = params
    const resp = await apiClient.formPut<ResponseModel<void>>({
      data: { roleIds },
      checkToken: true,
      url: '/api/role/privilege/refresh',
      apiOptions: options
    })
    return resp.data!
  }

  /**
   * 设置角色权限列表 - 设置角色权限列表
   * @param request 查询条件
   */
  async setRolePrivileges(params: SetRolePrivilegesParam = {}): Promise<void> {
    const {  request, options  } = params
    const resp = await apiClient.formPut<ResponseModel<void>>({
      data: { ...(request || {}) },
      checkToken: true,
      url: '/api/role/privilege',
      apiOptions: options
    })
    return resp.data!
  }

  /**
   * 修改角色 - 修改角色信息
   * @param roleId 角色编号
   * @param request 角色信息
   */
  async updateRole(params: UpdateRoleParam = {}): Promise<void> {
    const {  roleId, request, options  } = params
    const resp = await apiClient.jsonPut<ResponseModel<void>>({
      data: { ...(request || {}) },
      checkToken: true,
      url: `/api/role/${roleId}`,
      apiOptions: options
    })
    return resp.data!
  }
}
export const roleApi = new RoleApi()
